/**
 * Оповещения от рисунков.
 *
 * Провёл линию тренда — хочешь знать, когда цена её пересечёт. Отметил
 * зону — когда в неё войдёт. Нарисовал канал — когда из него выйдет.
 * Раньше для этого были отдельные инструменты «Линия-оповещение» и «Моя
 * зона»; теперь оповещение вешается на обычный рисунок кнопкой с
 * колокольчиком на его панели.
 *
 * Условие хранится в самом рисунке: подвинули — оповещение едет следом,
 * отменили — отменилось вместе с ним, удалили — ушло. Сервер следит за
 * ценой по записи в профиле, поэтому будит и при закрытой вкладке.
 */

import type { AlertLine, PriceAlert, PriceAlertCondition, PriceAlertRepeat } from '@specter/types';
import type { Candle } from '../state/store.js';
import { channelOf } from './channels.js';
import { regressionFit } from './regression.js';
import { shapeOptionsOf } from './shape-options.js';
import type { Drawing } from './types.js';

/** Что задал человек; где именно граница — берётся из рисунка. */
export interface DrawingAlert {
  readonly condition: PriceAlertCondition;
  readonly repeat: PriceAlertRepeat;
  /** Своя подпись: придёт в уведомлении вместо описания условия. */
  readonly note?: string;
  /**
   * Когда условие задали или поправили.
   *
   * По нему видно, что оповещение настроили заново: сработавшее
   * однократное после правки снова в деле.
   */
  readonly since: number;
}

/** Чем рисунок становится для сторожа цены. */
export type AlertShape = 'level' | 'line' | 'zone' | 'channel';

const SHAPES: Readonly<Record<string, AlertShape>> = {
  horizontal: 'level',
  'horizontal-ray': 'level',
  trend: 'line',
  ray: 'line',
  extended: 'line',
  'info-line': 'line',
  'trend-angle': 'line',
  rect: 'zone',
  'parallel-channel': 'channel',
  'flat-channel': 'channel',
  'disjoint-channel': 'channel',
  regression: 'channel',
};

export function alertShapeOf(kind: string): AlertShape | null {
  return SHAPES[kind] ?? null;
}

/** Можно ли повесить оповещение на рисунок этого вида. */
export function canAlert(kind: string): boolean {
  return kind in SHAPES;
}

export interface ConditionChoice {
  readonly value: PriceAlertCondition;
  readonly label: string;
  /** Одной фразой — что именно разбудит. */
  readonly hint: string;
}

/** Условия по форме рисунка — словами человека, первым идёт самое частое. */
export const CONDITIONS: Readonly<Record<AlertShape, readonly ConditionChoice[]>> = {
  level: [
    { value: 'cross', label: 'Пересечение', hint: 'Цена прошла уровень в любую сторону.' },
    { value: 'up', label: 'Вверх ↑', hint: 'Цена пробила уровень снизу вверх.' },
    { value: 'down', label: 'Вниз ↓', hint: 'Цена пробила уровень сверху вниз.' },
  ],
  line: [
    { value: 'cross', label: 'Пересечение', hint: 'Цена пересекла линию в любую сторону. Линия продолжается вправо.' },
    { value: 'up', label: 'Вверх ↑', hint: 'Цена пробила линию снизу вверх.' },
    { value: 'down', label: 'Вниз ↓', hint: 'Цена пробила линию сверху вниз.' },
  ],
  zone: [
    { value: 'enter', label: 'Вход в зону', hint: 'Цена была снаружи и вошла в прямоугольник по цене.' },
    { value: 'exit', label: 'Выход из зоны', hint: 'Цена была внутри и вышла за верх или низ.' },
  ],
  channel: [
    { value: 'exit', label: 'Выход', hint: 'Цена пробила канал вверх или вниз. Границы продолжаются вправо.' },
    { value: 'enter', label: 'Вход', hint: 'Цена была за каналом и вернулась внутрь.' },
    { value: 'cross', label: 'Касание', hint: 'Цена дошла до любой из границ.' },
  ],
};

export function defaultCondition(shape: AlertShape): PriceAlertCondition {
  return CONDITIONS[shape][0]!.value;
}

type Geometry = Pick<PriceAlert, 'price'> & Partial<Pick<PriceAlert, 'zone' | 'line' | 'channel'>>;

function line(from: { time: number; price: number }, to: { time: number; price: number }): AlertLine | null {
  if (from.time === to.time) return null;
  const [a, b] = from.time < to.time ? [from, to] : [to, from];
  return { from: { time: a.time, price: a.price }, to: { time: b.time, price: b.price } };
}

/**
 * Где проходит граница оповещения — по рисунку.
 *
 * `null`, если рисунок её не задаёт: вертикальная линия тренда, регрессия
 * без свечей под ней. Такое оповещение не создаётся, а уже созданное
 * остаётся с прежней границей.
 */
export function alertGeometry(drawing: Drawing, candles: readonly Candle[] = []): Geometry | null {
  const shape = alertShapeOf(drawing.kind);
  const [a, b] = drawing.points;
  if (!shape || !a) return null;

  if (shape === 'level') return a.price > 0 ? { price: a.price } : null;

  if (!b) return null;
  if (shape === 'line') {
    const trend = line(a, b);
    return trend ? { price: b.price, line: trend } : null;
  }

  if (shape === 'zone') {
    const low = Math.min(a.price, b.price);
    const high = Math.max(a.price, b.price);
    return low > 0 && high > low ? { price: (low + high) / 2, zone: { low, high } } : null;
  }

  // Каналы — ровно те границы, что нарисованы.
  if (drawing.kind === 'regression') {
    const fit = regressionFit(candles, a.time, b.time);
    if (!fit) return null;
    const band = fit.deviation * shapeOptionsOf(drawing.options).deviation;
    const upper = line({ time: fit.first.time, price: fit.start + band }, { time: fit.last.time, price: fit.end + band });
    const lower = line({ time: fit.first.time, price: fit.start - band }, { time: fit.last.time, price: fit.end - band });
    return upper && lower ? { price: fit.end, channel: { first: upper, second: lower } } : null;
  }
  const channel = channelOf(
    drawing.kind,
    drawing.points.map((point) => ({ x: point.time, y: point.price })),
  );
  if (!channel) return null;
  const toPoint = (p: { x: number; y: number }): { time: number; price: number } => ({ time: p.x, price: p.y });
  const first = line(toPoint(channel.first[0]), toPoint(channel.first[1]));
  const second = line(toPoint(channel.second[0]), toPoint(channel.second[1]));
  return first && second ? { price: (a.price + b.price) / 2, channel: { first, second } } : null;
}

/** Условие, подходящее рисунку: сохранённое чужой формы заменяется первым своим. */
export function conditionFor(shape: AlertShape, condition: PriceAlertCondition): PriceAlertCondition {
  return CONDITIONS[shape].some((choice) => choice.value === condition) ? condition : defaultCondition(shape);
}

/** Приведение сохранённого условия к текущей форме. */
export function normalizeDrawingAlert(value: unknown): DrawingAlert | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Record<string, unknown>;
  const conditions: readonly string[] = ['cross', 'up', 'down', 'enter', 'exit'];
  const condition = conditions.includes(raw.condition as string) ? (raw.condition as PriceAlertCondition) : 'cross';
  return {
    condition,
    repeat: raw.repeat === 'every' ? 'every' : 'once',
    ...(typeof raw.note === 'string' && raw.note.trim() !== '' ? { note: raw.note.trim().slice(0, 200) } : {}),
    since: typeof raw.since === 'number' && Number.isFinite(raw.since) ? raw.since : 0,
  };
}
